import React, { useEffect, useMemo, useState } from "react";

import PhoneIcon from "../../assets/icons/Call.svg";

interface ContactApiItem {
  id?: number;
  attributes?: {
    name?: string;
    phoneNumber?: string;
    phone?: string;
    region?: string;
    country?: string;
  };
  name?: string;
  phoneNumber?: string;
  phone?: string;
  region?: string;
  country?: string;
}

interface Contact {
  id: number;
  name: string;
  phoneNumber: string;
  region: string;
  country: string;
}

type ContactGroup = Record<string, Contact[]>;

const normalizeContacts = (items: ContactApiItem[]): Contact[] => {
  return items
    .map((item, index) => {
      const source = item.attributes ?? item;

      return {
        id: item.id ?? index,
        name: source?.name ?? "",
        phoneNumber: source?.phoneNumber ?? source?.phone ?? "",
        region: source?.region ?? "",
        country: source?.country ?? "",
      };
    })
    .filter((contact) => contact.name && contact.phoneNumber);
};

const groupContacts = (list: Contact[], key: (contact: Contact) => string) => {
  return list.reduce<ContactGroup>((acc, contact) => {
    const groupKey = key(contact) || "Other";
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(contact);
    return acc;
  }, {});
};

const ContactsPage: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"kenya" | "other">("kenya");

  useEffect(() => {
    const controller = new AbortController();

    const fetchContacts = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/contacts?sort=name:asc`,
          {
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch contacts");
        }

        const data = await response.json();
        const normalized = normalizeContacts(data?.data ?? []);

        setContacts(normalized);
        setError(null);
      } catch (err) {
        if ((err as Error).name === "AbortError") {
          return;
        }
        setError("We couldn't load the contacts. Please try again shortly.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchContacts();

    return () => controller.abort();
  }, []);

  const kenyanContacts = useMemo(
    () =>
      contacts.filter(
        (contact) => contact.country.toLowerCase() === "kenya".toLowerCase()
      ),
    [contacts]
  );

  const otherContacts = useMemo(
    () =>
      contacts.filter(
        (contact) => contact.country.toLowerCase() !== "kenya".toLowerCase()
      ),
    [contacts]
  );

  const kenyanGroups = useMemo(
    () => groupContacts(kenyanContacts, (contact) => contact.region || "Kenya"),
    [kenyanContacts]
  );

  const otherCountryGroups = useMemo(
    () => groupContacts(otherContacts, (contact) => contact.country || "Global"),
    [otherContacts]
  );

  const activeGroups =
    activeTab === "kenya"
      ? Object.entries(kenyanGroups)
      : Object.entries(otherCountryGroups);

  const renderTabButton = (
    key: "kenya" | "other",
    label: string,
    count: number
  ) => {
    const isActive = activeTab === key;
    return (
      <button
        type="button"
        onClick={() => setActiveTab(key)}
        className={`flex items-center gap-3 rounded-full px-6 py-2 text-sm font-medium transition-colors ${isActive
          ? "bg-[#F8ECD0] text-[#1A2767]"
          : "text-[#7A7E92] hover:text-[#1A2767]"
          }`}
      >
        {label}
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${isActive ? "bg-white text-[#1A2767]" : "bg-[#EEF0FF] text-[#5A5F73]"
            }`}
        >
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className="bg-white">
      <section className="bg-[#FFF6E5] py-10">
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-xl uppercase tracking-wide text-[#B3924B]">
            Contacts & Regions
          </p>
          <h1 className="mt-4 text-4xl font-semibold text-[#1A2767] md:text-5xl">
            Find a Course Facilitator Near{" "}
            <span className="text-[#6D7EE5]">You</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-[#5F6479] md:text-lg">
            Get in touch with our regional offices across Kenya and our
            international locations. We’re here to help you with any questions
            or support your need.
          </p>
        </div>
      </section>

      <section className="mt-10">
        <div className="container mx-auto px-4">
          <div className="mx-auto flex w-fit flex-col gap-3 rounded-2xl bg-white/90 p-3 shadow-lg shadow-[#E6E9F3]/70 ring-1 ring-[#F2F2F7] md:flex-row md:items-center">
            {renderTabButton("kenya", "Kenyan Regions", Object.keys(kenyanGroups).length)}
            <span className="hidden h-6 w-px bg-[#ECECF3] md:block" />
            {renderTabButton(
              "other",
              "Other Countries",
              Object.keys(otherCountryGroups).length
            )}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <div className="text-center">
          <p className="text-sm uppercase tracking-wide text-[#B3924B]">
            {activeTab === "kenya" ? "Kenya Regional Contacts" : "Global Contacts"}
          </p>
          <h2 className="mt-2 text-3xl font-semibold text-[#1F2A44]">
            {activeTab === "kenya"
              ? "Find facilitators in your region across Kenya"
              : "Connect with facilitators in other countries"}
          </h2>
        </div>

        <div className="mt-12">
          {isLoading && (
            <div className="flex justify-center py-16">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#EBCB56] border-t-transparent" />
            </div>
          )}

          {error && !isLoading && (
            <div className="mx-auto max-w-xl rounded-2xl bg-[#FFF5F0] p-6 text-center text-[#8C3A23]">
              {error}
            </div>
          )}

          {!isLoading && !error && activeGroups.length === 0 && (
            <div className="mx-auto max-w-xl rounded-2xl bg-[#F5F7FB] p-6 text-center text-[#4F556A]">
              No contacts available yet for this category. Please check back
              soon.
            </div>
          )}

          {!isLoading && !error && activeGroups.length > 0 && (
            <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {activeGroups.map(([groupName, members]) => (
                <article
                  key={groupName}
                  className="flex flex-col gap-5 rounded-3xl border border-lightStroke bg-lightHover p-6 shadow-sm shadow-[#CFD8EE]/40 transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EEF1FF]">
                      <img
                        src={PhoneIcon}
                        alt="Phone Icon"
                        className="h-6 w-6 opacity-80"
                      />
                    </div>
                    <span className="rounded-full bg-[#F2F6FF] px-4 py-1 text-xs font-medium text-[#2A3B77]">
                      {groupName}
                    </span>
                  </div>

                  <div className="flex flex-col gap-4">
                    {members.map((contact) => (
                      <div
                        key={`${groupName}-${contact.id}-${contact.phoneNumber}`}
                        className="rounded-2xl bg-[#FAFBFF] p-4"
                      >
                        <p className="text-base font-semibold text-[#1F2A44]">
                          {contact.name}
                        </p>
                        <a
                          href={`tel:${contact.phoneNumber}`}
                          className="text-sm text-[#667197] underline-offset-2 hover:text-[#1A2767] hover:underline"
                        >
                          {contact.phoneNumber}
                        </a>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContactsPage;
